/**
 * 需要想定 多系列チャート (系列描画のみ。外部凡例は ForecastChartLegend に分離)。
 * design 参照: design/demand/forecast-today.html renderChart()
 *
 * デザイン準拠の主要仕様:
 * - type: 'stepAfter' (階段状、design の recharts 設定と同一)
 * - forecast 系列: Area (fillOpacity=0.15) + dot マーカー
 * - 高さ: 2 カラム時はスクロール可視領域いっぱい、stacked 時は可視領域 55%・上限
 *   720px、最低 380px (useForecastChartHeight)
 * - X軸: interval=1 (縦グリッドは 1 時間おき)。ラベルは対象日 0:00 起点の既定
 *   3 時間毎 + 表示範囲の前端で、幅が足りなければ 6 → 12 時間毎へ間引く
 *   (AC-DFD-006a-4、2026-09-02 改訂)。ラベルは -45° の斜め表記 (issue #6879)
 * - 凡例: Recharts 組込 Legend は使わず、ForecastCard が保持する hiddenKeys を
 *   controlled prop として受け取る (design L785 / JS L2978-3005 の checkbox 外部凡例。
 *   凡例 UI 自体は ForecastCard が weather-strip の「上」に配置する = design L737-803
 *   のカード内縦順序準拠)。露点トグルもこの凡例に統合する。
 * - プロバイダ比較系列 (spec AC-DFC-001/004): 過去に一度 design 未反映を理由として
 *   復元したが、spec 準拠調整 (demand-forecast-daily-spec-align) AC-09 で
 *   「予測プロバイダ切替」機能自体の削除が決定されたため撤去した (Toolbar.tsx
 *   冒頭コメント参照)。
 * - margin / 左右 YAxis 幅は `constants/chartLayout.ts` の共有定数
 *   (CHART_MARGIN / CHART_LEFT_AXIS_WIDTH / CHART_RIGHT_AXIS_WIDTH)。WeatherBar が
 *   同定数を参照して天気帯の時刻セルをチャート X 軸と横位置整列させる
 *   (weather-icon-unify-48koma 後続 slice、design の weather-strip 整列要求)。
 * - 左 Y軸は数値のみ (単位サフィックスなし、design L3170-3172 準拠。単位はカード
 *   見出し「単位：kWh」〈KomaCard、demand-fix-48-koma で MWh から統一〉が担う)
 * - 左右 Y軸の domain / 目盛りは `models/chartAxis.ts` の純関数で算出する
 *   (spec AC-005b: 需要系列〈左軸〉と気温・露点系列〈右軸〉の描画帯を分離する。
 *   design L4296-4398 準拠 = 需要軸は 0 起点 + nice step、温度軸は上側余白を
 *   大きく取って下部帯へ寄せ目盛りは実データ範囲のみ)。**両軸とも凡例 OFF の
 *   系列は domain 入力に含めない** (非表示系列で軸が広がって残した系列が
 *   潰れるのを避ける。 需要軸は issue #6714 で温度軸に揃えた)
 * - 需要想定値の最大電力アノテーション (spec AC-005c、demand-forecast-spec-
 *   compliance AC-03/04): `models/slotHelpers.ts` の `computePeakLabel` で
 *   ピークコマ + kWh 換算 (30分値×1000、千区切り) 済みラベルを算出、値が空の
 *   場合は非表示 (demand-fix-48-koma で瞬時電力換算 ×2 を廃止し kWh 換算に統一)。
 *   AC-03 (spec 準拠改修 demand-forecast-daily-v4) により前日実績
 *   系列・最小電力ラインは過剰実装として削除済み (design には存在するが spec
 *   非準拠のため)。GC 縦線は AC-DFD-006a-5 (2026-10-01 PdM 判断) で当日断面に
 *   限って戻した。
 * - 参照列見出しのホバーで系列を強調・減光する (AC-DFD-006a-6、規則は
 *   `models/chartSeriesEmphasis.ts`)。
 *
 * チャートドラッグ編集 (AC-006、design L2093-2266 準拠):
 * - 需要想定値 (forecast) の点を mousedown で掴み、document mousemove (rAF 集約) で
 *   Y座標→値変換して即時プレビュー、mouseup で確定して onDragEdit に委譲する。
 * - Y軸 pixel↔値変換は描画済みの左 Y軸 tick を DOM から読む (design L2102-2131 [A])。
 *   数値計算 (線形変換の構築 / 比例配分 / 単位変換 / 誤掴み防止) は
 *   `models/chartDrag.ts` の純関数に切り出しユニットテストで担保する。
 * - isEditable=false (BG全体表示 / 完了ケース) の場合はドラッグを一切受け付けない。
 *
 * 旧シフト複数選択 + 一括補正 (旧 spec AC-006b、demand-forecast-spec-compliance
 * AC-05〜08、story demand-005/demand-008) は demand-fix-edit-operation で撤去
 * 済み (P0 spec に選択集合を要求する AC が無く、P1 の B 方式〈シフト = 比例配分の
 * 一時トリガー〉は集合概念を必要としないため保持根拠が無かった)。
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import type { PastActual } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { useTranslation } from 'react-i18next'
import type { MouseHandlerDataParam } from 'recharts'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  Box,
  LightModeSemanticColors,
  Text,
} from '@enechain/ecloud-designsystem'

import { ChartTooltip } from '~/components/common/ChartTooltip'
import { DEMAND_FORECAST_SERIES_COLORS } from '~/constants/chartPalette'
import { komaNumberToTime } from '~/models/komaToTime'
import {
  CHART_LEFT_AXIS_WIDTH,
  CHART_MARGIN,
  CHART_RIGHT_AXIS_WIDTH,
} from '~/pages/DemandForecastDaily/constants/chartLayout'
import { useChartGcLineKoma } from '~/pages/DemandForecastDaily/hooks/useChartGcLineKoma'
import { useForecastChartHeight } from '~/pages/DemandForecastDaily/hooks/useForecastChartHeight'
import { useForecastChartWidth } from '~/pages/DemandForecastDaily/hooks/useForecastChartWidth'
import {
  canShowFrontEdgeLabel,
  collectSeriesValues,
  computeChartAxisConfig,
  computeDemandAxisWidth,
  computePlotWidth,
  formatDemandTickLabel,
  resolveAxisSeriesKeys,
  resolveDemandDomainValues,
  resolveXAxisLabelStepHours,
} from '~/pages/DemandForecastDaily/models/chartAxis'
import {
  buildYAxisScale,
  computeDragTick,
  extractYAxisTickPoints,
  findLeftYAxisLabelGroup,
  isForecastGrabTarget,
  komaToChartIndex,
  resolveDragKoma,
  resolveDragMode,
  resolveHoverWeatherHour,
  shiftRecomputeClientY,
} from '~/pages/DemandForecastDaily/models/chartDrag'
import type { DragTickResult } from '~/pages/DemandForecastDaily/models/chartDrag'
import { pastDewSeriesDescriptors } from '~/pages/DemandForecastDaily/models/chartLegend'
import {
  emphasizedStrokeWidth,
  seriesEmphasis,
} from '~/pages/DemandForecastDaily/models/chartSeriesEmphasis'
import {
  EXTENSION_FORECAST_NEXT_KEY,
  EXTENSION_FORECAST_PREV_KEY,
  formatChartTooltipValue,
  nextDayExtensionTime,
  visibleTooltipEntries,
} from '~/pages/DemandForecastDaily/models/chartTooltip'
import {
  MAX_CONFIRM_RECORD_REFS,
  type ResolvedConfirmRecordRefColumn,
} from '~/pages/DemandForecastDaily/models/confirmRecordRef'
import type { ForecastSectionView } from '~/pages/DemandForecastDaily/models/forecastSectionView'
import {
  CHART_SLOT_COUNT,
  DAY_BOUNDARY_PRE_EXTENSION_KOMAS,
  MAX_CHART_PAST_ACTUALS,
  computePeakLabel,
  formatSlotValue,
  hasDefinedProtoSlotValue,
  protoSlotsToKoma,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

type Props = {
  /**
   * 断面の描画専用値型 (系列 10 個 + 存在述語 + 日付派生 + 編集 gate)。
   * `buildForecastSectionView` (index.tsx の単一 call site が呼ぶ) の出力を
   * そのまま受け取り、ここでは gate・述語を再計算しない
   * (`models/forecastSectionView.ts` の field JSDoc 参照)。
   */
  view: ForecastSectionView
  /** 外部凡例 (ForecastChartLegend) で非表示にした系列 key の集合 (controlled) */
  hiddenKeys: Set<string>
  /**
   * AC-15 (spec §2 機能詳細「列ホバー時の強調」): 48コマテーブルの列 (時間帯)
   * にホバー中のコマ番号。undefined は強調なし。
   */
  highlightKoma?: number
  /**
   * AC-DFD-006a-6: 48コマ表の参照列見出しにホバー中の系列のグループ key
   * (`todayActual` / `past_{idx}` / `confirmRef_{id}`)。 undefined は強調なし。
   * 過去実績は同じ日の気温・露点もまとめて残す。
   */
  emphasizedSeriesKey?: string
  /**
   * チャート上のホバー位置が変わった時に、気象帯で強調する「時」 (0〜23) を返す。
   * チャート外へ出たら undefined。 気象アイコンの強調 (2026-09-18 デザイナー指摘
   * 「グラフ上にホバーした際、 グラフの上の対象の気象アイコンをわかりやすくしたい」)
   * に使う。 チャート自身は強調を持たないので、 状態は親 (ForecastCard) が持つ。
   */
  onHoverWeatherHourChange?: (hour: number | undefined) => void
  /**
   * チャートより上のカード内 chrome (凡例・実績トレンドの帯・気象帯など) の
   * 構成が変わった時に値が変わる key。チャート高の再測定トリガー
   * (`useForecastChartHeight` の同名引数の JSDoc 参照)。
   */
  aboveChartRevision?: string | number | boolean
  /** 補正モード (design L742-756): 0=対象コマのみ変更、1=以降へ比例配分。既定 0 */
  dragMode?: 0 | 1
  /**
   * ドラッグ確定 (mouseup) 時のコールバック。mode は確定時の実効補正モード
   * (シフトキー押下中は dragMode に関わらず 1、AC-DFD-069)。確定側は mode で
   * 比例配分を分岐し、dragMode と別に OR を持たない。
   */
  onDragEdit?: (
    koma: number,
    value: number,
    nextSlots: KomaSlots,
    mode: 0 | 1,
  ) => void
  /**
   * 左 YAxis の幅が動的に変化した時のコールバック。
   * 気象バー (WeatherBar) の左スペーサーとチャート YAxis を揃えるため、
   * ForecastCard が本値を WeatherBar へ伝播する (fix #5432)。
   */
  onLeftAxisWidthChange?: (width: number) => void
  /**
   * 対象日の気温・天気が予測・実績とも 1 件も無いか (AC-DFC-013)。 true の時は
   * 気温の表示位置 (プロット領域の右上、 右軸の内側) に「この日の気象データは
   * ありません」を出す。 前日・翌日の延長部と類似日の気温の描画は変えない。
   * 判定は気象情報バーと同じ `isTargetDayWeatherMissing` の値を受け取る。
   */
  isTargetDayWeatherMissing?: boolean
}

/** チャートドラッグの掴み判定で forecast と比較する、需要系 (デマンドドメイン) の他系列 key。
 * 気温・露点は別軸 (yAxisId="temp") のため比較対象に含めない。 */
function buildOtherSeriesKeys(
  pastActualsCount: number,
  confirmRecordRefs: ResolvedConfirmRecordRefColumn[],
): string[] {
  const keys = ['todayActual', 'trendForecast']
  for (let i = 0; i < pastActualsCount; i++) keys.push(`past_${String(i)}`)
  for (const ref of confirmRecordRefs) keys.push(`confirmRef_${ref.id}`)
  return keys
}

/**
 * 描画済みチャートの左 Y軸から目盛りの pixel↔値マッピングを構築する
 * (design forecast-today.html L2107-2131 `_dftReadYScale` 準拠)。左軸ラベル群の特定は
 * `findLeftYAxisLabelGroup`、tick の抽出は `extractYAxisTickPoints`、線形変換の算出は
 * `buildYAxisScale` (いずれも純関数、chartDrag.test.ts でテスト済み) に委譲する。
 */
function readYAxisScale(
  container: HTMLElement | null,
): ((clientY: number) => number) | undefined {
  if (container === null) return undefined
  const leftAxis = findLeftYAxisLabelGroup(container)
  if (leftAxis === undefined) return undefined
  return buildYAxisScale(extractYAxisTickPoints(leftAxis))
}

type BuildChartDataParams = {
  forecastSlots: KomaSlots
  pastActuals: PastActual[]
  /** 確定履歴の参照列 (temperature / dewpoint は持たないため slots のみ)。既定 []。 */
  confirmRecordRefs?: ResolvedConfirmRecordRefColumn[]
  todayActualSlots: KomaSlots | undefined
  trendForecastSlots: KomaSlots | undefined
  temperatureForecastSlots: KomaSlots | undefined
  temperatureActualSlots: KomaSlots | undefined
  dewpointActualSlots: KomaSlots | undefined
  preTemperatureActualSlots?: KomaSlots
  preDewpointActualSlots?: KomaSlots
  /**
   * AC-DFD-006a-1: 前端の需要想定 (前日 koma 47・48 = 23:00・23:30)。前日を対象日とする
   * 業務確定が無い時は undefined (`forecastPrev` の key を置かない)。
   */
  preForecastSlots?: KomaSlots
  /**
   * AC-DFD-006a-1: 後端の需要想定 (翌日 koma 1〜6 = 0:00〜2:30)。翌日を対象日とする
   * 業務確定が無い時は undefined (`forecastNext` の key を置かない)。
   */
  postForecastSlots?: KomaSlots
  /** 前日 2 コマの時刻ラベルを "前日{{time}}" 等に整形する関数。未指定時は time のみ。 */
  formatPreviousDayTime?: (time: string) => string
}

/** 後端の需要想定のコマ数 (翌日 koma 1〜6 = 0:00〜2:30、proto `ExtensionForecastSlots.post_slots`)。 */
const DAY_BOUNDARY_POST_FORECAST_KOMAS = 6

/**
 * 延長部の需要想定の参考系列 (AC-DFD-006a-1) の 1 点の値。design
 * `demand/forecast-today.html` に合わせ、対象日の需要想定 (`forecast`) とは別の参考線で描く。
 *
 * - `forecastPrev`: 前日 2 点に前日 koma 47・48、0:00 の点に対象日 koma 1 (需要想定の
 *   始端に接続する)
 * - `forecastNext`: 23:30 の点に対象日 koma 48 (接続の起点)、24:00〜26:30 に翌日 koma 1〜6、
 *   27:00 に stepAfter の終端として翌日 koma 6
 *
 * 前日・翌日の業務確定が無い側 (slots が undefined) は key を置かない (線を描かない)。参考値は表示のみで、編集・
 * ドラッグ・ピーク注記・48 コマ表・合計には入らない。
 */
function extensionForecastPoint(
  forecastSlots: KomaSlots,
  preForecastSlots: KomaSlots | undefined,
  postForecastSlots: KomaSlots | undefined,
  slotIdx: number,
  pointIdx: number,
): { forecastPrev?: number | null; forecastNext?: number | null } {
  const lastIdx = forecastSlots.length - 1
  const point: { forecastPrev?: number | null; forecastNext?: number | null } =
    {}
  if (preForecastSlots !== undefined)
    point.forecastPrev =
      slotIdx < 0
        ? (preForecastSlots[pointIdx] ?? null)
        : slotIdx === 0
          ? // 対象日 koma 1 が空でも前日 koma 48 の線を 0:00 まで引く終端点にする
            (forecastSlots[0] ?? preForecastSlots[pointIdx - 1] ?? null)
          : null
  if (postForecastSlots !== undefined)
    point.forecastNext =
      slotIdx < lastIdx
        ? null
        : slotIdx === lastIdx
          ? (forecastSlots[lastIdx] ?? null)
          : (postForecastSlots[
              Math.min(
                slotIdx - lastIdx - 1,
                DAY_BOUNDARY_POST_FORECAST_KOMAS - 1,
              )
            ] ?? null)
  return point
}

export function buildChartData({
  forecastSlots,
  pastActuals,
  confirmRecordRefs = [],
  todayActualSlots,
  trendForecastSlots,
  temperatureForecastSlots,
  temperatureActualSlots,
  dewpointActualSlots,
  preTemperatureActualSlots,
  preDewpointActualSlots,
  preForecastSlots,
  postForecastSlots,
  formatPreviousDayTime = (time: string) => time,
}: BuildChartDataParams): Array<Record<string, number | null | string>> {
  // 需要系 (demand) の値。保存値がそのまま表示値 (送電端 kWh/30min、AC-DFC-011)
  // なので単位変換はなく、Recharts が要求する null 埋めだけを行う。
  const conv = (v: number | undefined): number | null => v ?? null

  /**
   * 末尾 24:00 ダミー点の値 (issue #6721)。
   *
   * X 軸は日境界を内部に描くため 24:00 の点を持つ (前日延長 2 + 48 コマ + 24:00〜27:00
   * の 57 点)。 `stepAfter` は
   * 「次の点まで水平に伸ばす」 描画なので、 **24:00 の点に値が無いと最終コマ
   * (23:30) の水平線が引かれず、 線が 23:30 で切れて見える**。 需要想定値だけは
   * 以前からここで最終コマ値を保持していたが、 類似日 (過去実績) ・確定履歴参照列・
   * 当日実績・実績トレンドは取りこぼしていた (デザイナー報告 2026-08-31)。
   *
   * 保持するのは**最終コマの値**なので、 データがそこまで無い系列 (当日実績が
   * 現在コマまでしか無い等) は `undefined` のままとなり、 線はデータの終わりで
   * 止まる (24:00 まで引き伸ばさない)。
   *
   * 気温・露点 (予測・実績とも) は `atKoma` を使わず素の添字参照で取るため、48 コマ
   * 配列では 24:00 が null になる (その時刻にデータがある時だけ出す)。54 コマ配列
   * では index 48 が実データなのでそのまま出る。予測気温は正時の点の値だけを持つ
   * ので (AC-WD-003a)、24:00 には 23:30 の値を複写せず、対象日の 24 時の点 (54 コマ
   * 配列の index 48) がある時だけ描く (AC-DFD-006a-2 の ※)。
   */
  const atKoma = (
    komas: ArrayLike<number | undefined>,
    slotIdx: number,
  ): number | null =>
    slotIdx === komas.length
      ? conv(komas[komas.length - 1])
      : conv(komas[slotIdx])

  const pastKomas = pastActuals.map((pa) => protoSlotsToKoma(pa.slots))
  // AC-23: 選択過去日の実績気温 (temperature_slots)。need not exist per pastActual
  // (backend graceful degrade); protoSlotsToKoma(undefined-safe) mirrors past
  // demand komas above but only when temperatureSlots is non-empty.
  const pastTempKomas = pastActuals.map((pa) =>
    pa.temperatureSlots.length > 0
      ? protoSlotsToKoma(pa.temperatureSlots)
      : undefined,
  )
  // AC-05/AC-07 (TC-D-40.2): 選択過去日の実績露点 (dewpoint_slots)。露点実績を
  // 持たない過去日は undefined のまま (= キー不生成、pastTempKomas と同型)。
  // 実績未到着の過去日は全コマ欠測の 48 件配列で届くため、配列長でなく定義済み
  // スロットの有無で判定する (pastDewSeriesDescriptors と同じ述語)。
  const pastDewKomas = pastActuals.map((pa) =>
    hasDefinedProtoSlotValue(pa.dewpointSlots)
      ? protoSlotsToKoma(pa.dewpointSlots)
      : undefined,
  )
  // AC-DFD-006a: 対象日によらず前日 23:00〜翌日 3:00 で組み、日境界 (0:00 / 24:00) を
  // チャート内部に描く。
  return Array.from({ length: CHART_SLOT_COUNT }, (_, i) => {
    // 前日 2 コマ (23:00/23:30) の後に対象日 00:00 が続く。logicalKoma=0 (24:00) は
    // 存在しないため、前日領域を 1 つずらして skip する。
    const logicalKoma =
      i < DAY_BOUNDARY_PRE_EXTENSION_KOMAS
        ? i - DAY_BOUNDARY_PRE_EXTENSION_KOMAS
        : i + 1 - DAY_BOUNDARY_PRE_EXTENSION_KOMAS
    const slotIdx = logicalKoma - 1
    const komaLabel =
      logicalKoma < 1
        ? formatPreviousDayTime(komaNumberToTime(logicalKoma + 49))
        : komaNumberToTime(logicalKoma)
    const point: Record<string, number | null | string> = { koma: komaLabel }

    // 需要想定値: 対象日コマのみ。末尾 24:00 ダミー点だけ stepAfter で対象日領域を境界まで
    // 到達させるため最終コマ値を保持する。延長部は参考系列 (forecastPrev / forecastNext) で描く。
    point['forecast'] = atKoma(forecastSlots, slotIdx)
    Object.assign(
      point,
      extensionForecastPoint(
        forecastSlots,
        preForecastSlots,
        postForecastSlots,
        slotIdx,
        i,
      ),
    )

    for (let j = 0; j < pastKomas.length; j++) {
      const komas = pastKomas[j]
      if (komas !== undefined)
        point[`past_${String(j)}`] = atKoma(komas, slotIdx)
    }
    // AC-DFD-031 (s07-confirm-history-import-reference-view.md §UI「チャート
    // 系列」): 確定履歴の参照列。temperature / dewpoint は確定履歴に存在しない
    // ため、需要系列 (slots) のみ描画する (pastActuals の温度/露点ループは複製しない)。
    for (let j = 0; j < confirmRecordRefs.length; j++) {
      const ref = confirmRecordRefs[j]
      if (ref !== undefined)
        point[`confirmRef_${ref.id}`] = atKoma(ref.slots, slotIdx)
    }
    // AC-23: 過去実績気温 (℃のまま、単位変換しない — 気温/露点と同じ扱い)
    for (let j = 0; j < pastTempKomas.length; j++) {
      const komas = pastTempKomas[j]
      if (komas !== undefined)
        point[`pastTemp_${String(j)}`] = komas[slotIdx] ?? null
    }
    // AC-05/AC-07: 過去実績露点 (℃のまま、単位変換しない)。欠測コマは値なし
    // (null) のまま保持する (TC-D-40.2)。
    for (let j = 0; j < pastDewKomas.length; j++) {
      const komas = pastDewKomas[j]
      if (komas !== undefined)
        point[`pastDew_${String(j)}`] = komas[slotIdx] ?? null
    }
    if (todayActualSlots !== undefined)
      point['todayActual'] = atKoma(todayActualSlots, slotIdx)
    if (trendForecastSlots !== undefined)
      point['trendForecast'] = atKoma(trendForecastSlots, slotIdx)
    // 気温・露点は℃のまま (単位変換しない)。予測気温は正時の点のコマだけが値を
    // 持ち、点以外のコマは null のまま (補間しない、AC-WD-003a。折れ線は
    // connectNulls で点どうしを結び、ツールチップは null の系列を出さない)。
    // AC-DFD-006a-2 で前端・後端 (slotIdx > 48) は出さない。
    if (temperatureForecastSlots !== undefined && slotIdx >= 0 && slotIdx <= 48)
      point['tempForecast'] = temperatureForecastSlots[slotIdx] ?? null
    if (slotIdx < 0 && preTemperatureActualSlots !== undefined)
      point['tempActual'] = preTemperatureActualSlots[i] ?? null
    // 実績は「その時刻にデータがある時だけ」出す = `atKoma` の最終コマ保持を使わない。
    // design `demand/forecast-today.html` の 24:00 ダミー点 (`_dummy`) が
    // `actual` / `dewActual` に null を置いているのと同じ切り分け。54 コマ配列なら index 48 が実データなので
    // 通常の添字参照でそのまま出る (過去実績の pastTemp / pastDew と同じ形)。
    if (temperatureActualSlots !== undefined && slotIdx >= 0)
      point['tempActual'] = temperatureActualSlots[slotIdx] ?? null
    if (slotIdx < 0 && preDewpointActualSlots !== undefined)
      point['dewActual'] = preDewpointActualSlots[i] ?? null
    if (dewpointActualSlots !== undefined && slotIdx >= 0)
      point['dewActual'] = dewpointActualSlots[slotIdx] ?? null
    return point
  })
}

/**
 * AC-DFD-006a-4: X軸ラベルの刻み (時間) の既定値。対象日 0:00 を起点にこの倍数の
 * 正時だけラベルを出す。
 *
 * 幅が足りない時は `resolveXAxisLabelStepHours` が 6 / 12 へ間引く
 * (design も同じ。2026-08-31 デザイナー指摘で追随)。 幅を測れない時はこの値。
 */
const X_AXIS_LABEL_STEP_HOURS = 3

/**
 * AC-DFD-006a-4: X軸の時刻ラベルを出す tick かどうかを **値** で判定する。
 *
 * 旧実装は tick の index (`idx % 2 === 0`) で数えていた。現状のデータは koma 1 = 0:00 から
 * 始まるので結果は 0:00 起点の 2 時間毎になるが、AC-DFD-006a (P0、チャート範囲を
 * 前日 23:00 まで前方へ延長する) を実装すると **先頭に前日ぶんの点が入って位相がずれ、
 * 0:00 のラベルが出なくなる**。design prototype (`design/demand/forecast-today.html`) は
 * 既に範囲拡張済みで、まさにこの理由で 1:00 起点の奇数時ラベルになっていた (#5059)。
 * データ配列の先頭がどこであってもラベルの位置が動かないよう、値で判定する。
 *
 * **前端 (index 0) は常にラベルを出す。** AC-DFD-006a-4 は 3 時間毎に「加えて、
 * 表示範囲の前端 (前日 23:00) のラベルも表示する」
 * と定めている。 前端の値は `buildChartData` が i18n 経由で `前日23:00`
 * (英語では `Prev 23:00`) を付けるため `HH:00` に一致せず、 値だけで判定すると
 * 消えてしまう。 文言でのマッチは locale 依存で脆いので、 **位置 (index 0) で
 * 判定する** (range の前端 = 最初の tick、 という定義そのもの)。
 *
 * @param value `HH:MM` 形式の tick 値 (`extendedKomaToTimeLabel` の出力)
 * @param index tick の位置。 `0` (= 表示範囲の前端) は値に依らずラベルを出す
 */
/*
 * [2026-09-02 / issue #6879] `xAxisTickAnchor` を撤去した。
 *
 * ラベルを -45° の斜め表記にしたことで、前端「前日23:00」の水平揃えを特例扱いする
 * 必要が無くなったため (詳細は `renderAxisTick` の JSDoc)。あの特例は水平ラベルが
 * Y 軸側へはみ出すのを防ぐためのもので、斜めでははみ出さない。全 tick が
 * `textAnchor="end"` + `rotate(-45)` で揃う。
 */

export function isXAxisLabelTick(
  value: string,
  index?: number,
  stepHours: number = X_AXIS_LABEL_STEP_HOURS,
  /**
   * 前端 (index 0) のラベルを出せる幅か。`false` の時だけ前端を落とす。
   * **間引き (stepHours) では前端の重なりは解けない** ため別軸で受ける
   * (理由は `canShowFrontEdgeLabel` の JSDoc)。
   */
  showFrontEdge = true,
): boolean {
  if (index === 0) return showFrontEdge
  const m = /^(\d{1,2}):00$/.exec(value)
  return m !== null && Number(m[1]) % stepHours === 0
}

/**
 * AC-DFD-006c: ドラッグ補正中 (`dragPreview` 定義中、mousedown での掴み〜mouseup
 * 確定まで) は各系列値ツールチップを表示しない。判定を純関数へ切り出すのは、
 * Recharts の `<Tooltip>` は happy-dom で hover 描画されない既知制約 (本ファイル
 * 冒頭 ForecastChart.test.tsx コメント参照) があり、条件描画の分岐自体を固定
 * できる最小の検証 seam にするため (Design Note
 * `tmp/designing-feature/demand-fix-chart-tooltip.md` Decision #1/#2)。
 */
export function isChartTooltipSuppressed(
  dragPreview: { koma: number } | undefined,
): boolean {
  return dragPreview !== undefined
}

/**
 * 需要想定チャートの tooltip 本体。共通 `ChartTooltip` に 2 点だけ被せる。
 *
 * 1. 出す行を [[visibleTooltipEntries]] で絞る (末尾 24:00 のダミー点と、参考系列の接続点)
 * 2. 表示値の書式を系列ごとに切り替える (左軸 kWh は整数丸め / 右軸 ℃ は素通し)
 *
 * props を optional だけで受けるのは、`<Tooltip content={<X />} />` の形だと
 * recharts が実行時に cloneElement で注入するため JSX 上は空になるから
 * (共通 `ChartTooltip` も同じ理由で同じ形にしている)。
 */
const ForecastChartTooltipContent = (props: {
  active?: boolean
  label?: string | number
  payload?: ReadonlyArray<{
    name?: string | number
    value?: number | string
    color?: string
    dataKey?: string | number
  }>
}): React.ReactElement | null => {
  const { t } = useTranslation()
  const payload = visibleTooltipEntries(props.label, props.payload ?? [])
  const nextDayTime = nextDayExtensionTime(props.label)
  return payload.length === 0 ? null : (
    <ChartTooltip
      {...props}
      formatValue={(value, dataKey) => formatChartTooltipValue(dataKey, value)}
      label={
        nextDayTime === undefined
          ? props.label
          : t('page:demandForecastDaily.chart.nextDayTime', {
              time: nextDayTime,
            })
      }
      payload={payload}
    />
  )
}

const FORECAST_COLOR = DEMAND_FORECAST_SERIES_COLORS.forecast
const TODAY_ACTUAL_AREA_COLOR = DEMAND_FORECAST_SERIES_COLORS.todayActual
// 実績トレンド予測 (design forecast-today.html:6045 `text-warning-high`)
const TREND_FORECAST_COLOR = DEMAND_FORECAST_SERIES_COLORS.trendForecast
const TEMP_FORECAST_COLOR = DEMAND_FORECAST_SERIES_COLORS.tempForecast
// 気温は予測・実績とも同色 (`text-negative-mid`) で、破線 / 実線の線種で区別する
// (design forecast-today.html:6012 と :6018 が同じ tempFcColor を使う)
const TEMP_ACTUAL_COLOR = DEMAND_FORECAST_SERIES_COLORS.tempActual
// 露点実績 (design forecast-today.html:5543/:6029 `brand-teal`)
const DEW_ACTUAL_COLOR = DEMAND_FORECAST_SERIES_COLORS.dewpoint
const AXIS_COLOR = LightModeSemanticColors.Text.midEmphasis.value
const GRID_COLOR = LightModeSemanticColors.Border.lowEmphasis.value
// AC-15: 列ホバー強調マーカー色
const HIGHLIGHT_COLOR = LightModeSemanticColors.Text.accentPrimary.value
/** GC 縦線 (design `forecast-today.html` の gcColor = `--surface-negative-high`)。 */
const GC_LINE_COLOR = LightModeSemanticColors.Surface.negativeHigh.value
/** 強調した系列のデータ点の縁取り (design `_emphDot` の `--surface-primary`)。 */
const SURFACE_COLOR = LightModeSemanticColors.Surface.primary.value
/** 延長部の需要想定の参考線 (AC-DFD-006a-1、design `demand/forecast-today.html` の
 * `forecastPrev` / `forecastNext`)。 */
const EXTENSION_FORECAST_COLOR = LightModeSemanticColors.Text.lowEmphasis.value

// 過去実績線の dash。design forecast-today.html:5981 (`_dftRenderTable` = 実際に
// 描画されている経路) 準拠。同 :5586 にも同名の配列があるが、そちらは tooltip 名
// 生成用 seriesList 専用で描画には効かないため採用しない。
const PAST_DASHES = ['4 2', '1 3', '9 4', '10 3 2 3', '6 3 1 3']
const PAST_COLORS = DEMAND_FORECAST_SERIES_COLORS.past
// 確定履歴参照列は過去実績と別の色相 (青系) に加えて別の dash パターンでも区別する。
// dash は design forecast-today.html:5982 準拠 (過去実績 :5981 と重ならない配列)。
const CONFIRM_REF_DASHES = ['2 2', '8 4', '1 5', '11 3 2 3', '5 4 1 4']
const CONFIRM_REF_COLORS = DEMAND_FORECAST_SERIES_COLORS.confirmRef

// 気温存在述語 (右 YAxis 描画判定) は ForecastCard と共有する
// models/slotHelpers.ts の hasChartTemperatureData を使う (整列バグ回帰固定、
// JSDoc 参照)。ローカル定義は廃止した。
// AC-23: 選択過去日の実績気温判定も hasChartTemperatureData に含まれる。

/** チャートの「この日の気象データはありません」をプロット上端から下げる量 (px)。
 * GC 縦線のラベル 1 行 (fontSize 12 + recharts の inside offset 5) を避ける。 */
const NO_WEATHER_DATA_NOTICE_OFFSET_TOP = 24

export const ForecastChart = ({
  view,
  hiddenKeys,
  highlightKoma,
  emphasizedSeriesKey,
  aboveChartRevision,
  dragMode = 0,
  onDragEdit,
  onHoverWeatherHourChange,
  onLeftAxisWidthChange,
  isTargetDayWeatherMissing = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const {
    forecast: forecastSlots,
    todayActual: todayActualSlots,
    trendForecast: trendForecastSlots,
    temperatureForecast: temperatureForecastSlots,
    temperatureActual: temperatureActualSlots,
    dewpointActual: dewpointActualSlots,
    preTemperatureActual: preTemperatureActualSlots,
    preDewpointActual: preDewpointActualSlots,
    preForecast: preForecastSlots,
    postForecast: postForecastSlots,
    pastActuals: pastActualsProp,
    confirmRecordRefs: confirmRecordRefsProp,
  } = view.series
  const {
    hasTodayActual,
    hasTrendForecast,
    showTemperature,
    hasTempForecast,
    hasTempActual,
    hasDewpointData,
  } = view.presence
  const { gcKoma, gcLine } = view.cells
  const { isEditable, hasEditRow } = view.editing
  const { isReferenceView } = view.date
  const label = view.targetLabel
  // AC-DFD-006a-5: 当日断面だけ GC 縦線を出す (gcLine は builder が対象日 = 今日で gate 済み)
  const gcLineKoma = useChartGcLineKoma(gcLine)
  // AC-DFD-006a-6: 強調中の系列は太線・実線・データ点、ほかは減光 (需要想定値は対象外)
  const emphasisOf = (seriesKey: string): ReturnType<typeof seriesEmphasis> =>
    seriesEmphasis(emphasizedSeriesKey, seriesKey)
  const emphasizedDot = (
    color: string,
  ): { r: number; fill: string; stroke: string; strokeWidth: number } => ({
    r: 2.6,
    fill: color,
    stroke: SURFACE_COLOR,
    strokeWidth: 1,
  })
  const formatPreviousDayTime = (time: string): string =>
    t('page:demandForecastDaily.chart.previousDayTime', { time })

  // AC-01/AC-02 (spec AC-001/AC-005): 過去実績系列は最大5日分 (demand-forecast-
  // daily-spec-align で 3→5 に拡大、MAX_CHART_PAST_ACTUALS = hasChartTemperatureData
  // の判定スライスと同じ値を共有)。呼び出し元の実装差異に備える defensive cap。
  const pastActuals = pastActualsProp.slice(0, MAX_CHART_PAST_ACTUALS)
  // AC-DFD-031 (s07-confirm-history-import-reference-view.md Decision #3):
  // 確定履歴参照列は独立 5 件上限 (useConfirmRecordImportFlow.ts が state 自体を
  // MAX_CONFIRM_RECORD_REFS で既に上限管理しているが、呼び出し元の実装差異に
  // 備える defensive cap をここにも置く)。
  const confirmRecordRefs = confirmRecordRefsProp.slice(
    0,
    MAX_CONFIRM_RECORD_REFS,
  )

  // チャートドラッグ編集 (AC-006)。 latestRef に毎レンダー最新値を積み、
  // document mousemove/mouseup リスナー (mount 時に 1 度だけ登録) から常に最新の
  // props/state を読む (design L2183-2199 [B] の rAF 集約と同じ意図: リスナー自体は
  // 再登録せず、中身だけ最新化する)。
  const containerRef = useRef<HTMLDivElement>(null)
  const chartHeight = useForecastChartHeight(containerRef, aboveChartRevision)
  const containerWidth = useForecastChartWidth(containerRef)
  const dragKomaRef = useRef<
    { koma: number; scale: (clientY: number) => number } | undefined
  >(undefined)
  const rafIdRef = useRef(0)
  const pendingClientYRef = useRef<number | undefined>(undefined)
  const lastTickRef = useRef<
    { koma: number; value: number; slots: KomaSlots } | undefined
  >(undefined)
  // ドラッグ中のシフトキー状態 (AC-DFD-069・070)。mousedown / mousemove / mouseup
  // の shiftKey と、ドラッグ中の keydown / keyup で更新する
  const shiftPressedRef = useRef(false)
  // ドラッグ中プレビュー (state = チャートのみ再描画。テーブル反映は mouseup の
  // onDragEdit 経由、design L2179-2199 [B] の「グラフのみ即時、表は mouseup まで遅延」準拠)
  const [dragPreview, setDragPreview] = useState<
    { koma: number; value: number | undefined; slots: KomaSlots } | undefined
  >(undefined)

  const latestRef = useRef({
    isEditable,
    hasEditRow,
    gcKoma,
    dragMode,
    forecastSlots,
    onDragEdit,
  })
  latestRef.current = {
    isEditable,
    hasEditRow,
    gcKoma,
    dragMode,
    forecastSlots,
    onDragEdit,
  }

  useEffect(() => {
    // 比例配分の基準は編集前値 (= ドラッグ開始時点の需要想定値、AC-DFD-006 ※)。
    // ドラッグ中は forecastSlots (親 state) が mouseup まで不変なので、
    // この値がそのままドラッグ開始時点のスナップショットになる。取込原値
    // (baselineSlots=providerSlots) は基準にしない (未取込時に配分されない)。
    // シフトキーを離して実効モードが 0 に戻ると、編集コマより後のコマは
    // このスナップショットの値に戻る (AC-DFD-070)。
    const computeTickAt = (
      koma: number,
      displayValue: number,
    ): DragTickResult | undefined => {
      // note: 変数名は `props` にしない (react/prop-types の変数名ヒューリスティックが false positive で "missing in props validation" を報告する)。
      const latest = latestRef.current
      if (!latest.hasEditRow) return undefined
      return computeDragTick({
        baselineSlots: latest.forecastSlots,
        forecastSlots: latest.forecastSlots,
        koma,
        newDisplayValue: displayValue,
        dragMode: resolveDragMode(latest.dragMode, shiftPressedRef.current),
        gcKoma: latest.gcKoma,
      })
    }

    // 無効な位置 (負値など) では最後の有効な編集値を使い、現在の実効モードで
    // slots を組み直す (無効な位置で Shift を離しても以降のコマを戻す、AC-DFD-070)。
    const computeTick = (clientY: number): DragTickResult | undefined => {
      const drag = dragKomaRef.current
      if (drag === undefined) return undefined
      const tick = computeTickAt(drag.koma, drag.scale(clientY))
      if (tick !== undefined) return tick
      const last = lastTickRef.current
      return last === undefined
        ? undefined
        : computeTickAt(last.koma, last.value)
    }

    const scheduleTick = (): void => {
      if (rafIdRef.current !== 0) return
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = 0
        const clientY = pendingClientYRef.current
        if (clientY === undefined) return
        const tick = computeTick(clientY)
        if (tick === undefined) return
        lastTickRef.current = tick
        setDragPreview(tick)
      })
    }

    const handleMouseMove = (e: MouseEvent): void => {
      if (dragKomaRef.current === undefined) return
      shiftPressedRef.current = e.shiftKey
      pendingClientYRef.current = e.clientY
      scheduleTick()
    }

    // マウスを動かさずにシフトキーを押す・離すだけでも実効モードを切り替える
    // (AC-DFD-070)。ドラッグ外の Shift (48 コマ表の範囲選択など) には反応しない。
    const handleShiftKey = (e: KeyboardEvent): void => {
      if (dragKomaRef.current === undefined || e.key !== 'Shift') return
      const nextShift = e.type === 'keydown'
      const clientY = shiftRecomputeClientY({
        prevShift: shiftPressedRef.current,
        nextShift,
        lastClientY: pendingClientYRef.current,
      })
      shiftPressedRef.current = nextShift
      if (clientY !== undefined) scheduleTick()
    }

    const handleMouseUp = (e: MouseEvent): void => {
      if (dragKomaRef.current === undefined) return
      if (rafIdRef.current !== 0) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
      // keyup を取りこぼしても、確定値は離した時点のシフトキー状態に合わせる。
      // mousemove が無いドラッグ (クリックのみ) は従来どおり確定しない。
      shiftPressedRef.current = e.shiftKey
      const clientY = pendingClientYRef.current
      const finalTick =
        lastTickRef.current !== undefined && clientY !== undefined
          ? computeTick(clientY)
          : undefined
      const mode = resolveDragMode(
        latestRef.current.dragMode,
        shiftPressedRef.current,
      )
      dragKomaRef.current = undefined
      lastTickRef.current = undefined
      pendingClientYRef.current = undefined
      shiftPressedRef.current = false
      setDragPreview(undefined)
      if (finalTick !== undefined) {
        latestRef.current.onDragEdit?.(
          finalTick.koma,
          finalTick.value,
          finalTick.slots,
          mode,
        )
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('keydown', handleShiftKey)
    document.addEventListener('keyup', handleShiftKey)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('keydown', handleShiftKey)
      document.removeEventListener('keyup', handleShiftKey)
      if (rafIdRef.current !== 0) cancelAnimationFrame(rafIdRef.current)
    }
  }, [])

  const otherSeriesKeys = useMemo(
    () => buildOtherSeriesKeys(pastActuals.length, confirmRecordRefs),
    [pastActuals.length, confirmRecordRefs],
  )

  // showTemperature / hasTodayActual / hasTrendForecast / hasDewpointData /
  // hasTempForecast / hasTempActual は builder (`buildForecastSectionView`) が
  // ForecastCard (凡例構築) と共有する 1 箇所で計算済み (view.presence、整列
  // バグ回帰固定)。ここでは再計算しない。
  // AC-05/TC-D-40.2: 過去日露点系列の期待値固定点 (id・軸・トグル key)。
  const pastDewDescriptors = pastDewSeriesDescriptors(pastActuals)

  // ドラッグ中はプレビュー値をチャートにのみ反映する (親 state は mouseup まで不変)
  const effectiveForecastSlots = dragPreview?.slots ?? forecastSlots

  const data = buildChartData({
    forecastSlots: effectiveForecastSlots,
    pastActuals,
    confirmRecordRefs,
    todayActualSlots: hasTodayActual ? todayActualSlots : undefined,
    trendForecastSlots: hasTrendForecast ? trendForecastSlots : undefined,
    temperatureForecastSlots,
    temperatureActualSlots,
    dewpointActualSlots,
    preTemperatureActualSlots,
    preDewpointActualSlots,
    preForecastSlots,
    postForecastSlots,
    formatPreviousDayTime,
  })

  // spec AC-005b: 需要系列 (左軸) と気温・露点系列 (右軸) の描画帯を分離する。
  // 軸ごとの domain 入力系列は `resolveAxisSeriesKeys` が上の `data` の行に実在する
  // key から導く (温度軸は表示中の系列のみ / 需要軸は表示状態に依らず全系列 =
  // design L4300-4307)。系列の有無は `buildChartData` が行に key を置く条件と同義
  // なので、下の Line/Area の描画条件を手で対応させる必要はない (= 描画条件と
  // domain 入力のズレが構造的に起きない)。
  const axisSeriesKeys = resolveAxisSeriesKeys({
    data,
    showTemperature,
    hiddenKeys,
  })
  // 需要系は表示単位へ変換済みの data から集める (domain も表示単位に揃える)
  const demandValues = collectSeriesValues(data, axisSeriesKeys.demandKeys)
  const temperatureValues = collectSeriesValues(
    data,
    axisSeriesKeys.temperatureKeys,
  )
  // 需要軸 domain の入力 (issue #6714 / #6814)。 表示中のみ・フォールバック無しの
  // 規則は `resolveDemandDomainValues` が持つ (そこの docstring が正本)。
  const visibleDemandValues = resolveDemandDomainValues(
    data,
    axisSeriesKeys.demandKeys,
    hiddenKeys,
  )
  const hasVisibleDemandValues = visibleDemandValues.length > 0
  const axisConfig = computeChartAxisConfig({
    /**
     * 需要軸の domain は **表示中の需要系列だけ**から決める (issue #6714)。
     *
     * 以前は表示状態に依らず全系列を入力にしていた (「凡例トグルで需要カーブの
     * 高さが変わらない」 ため)。 だが需要想定値が過去実績より 1 桁大きい断面では、
     * 需要想定値を消しても軸の最大値がそのまま残り、 **残した過去実績が軸の
     * 下端に潰れて読めない** (デザイナー報告 2026-08-31)。 凡例を切る目的が
     * 「残した系列を読む」 ことなので、 軸が追随しないと目的を果たせない。
     * 温度軸は元から「表示中のみ」 (AC-04) で、 これで両軸の扱いが揃う。
     *
     * **全系列を隠した時も空配列のまま渡す。** ここで全系列に戻すと、
     * `computeChartAxisConfig` が同じ値を温度軸の帯分離 (`demandTroughRatio`)
     * にも使うため、 **見えていない需要カーブを避けようとして気温カーブが
     * 下端へ圧縮される** (devin review 指摘 #6814)。 需要が 1 本も見えない時は
     * 避ける相手が居ないのが正しい。 左軸自体は下の `hasVisibleDemandValues` で
     * 非表示にする。
     */
    demandValues: visibleDemandValues,
    temperatureValues,
  })

  /**
   * 左 YAxis の幅だけは **全系列**から決める。
   *
   * 幅は WeatherBar の左スペーサーと共有していて (fix #5432)、 凡例トグルで
   * 変わると気象帯とチャートの横位置整列がずれる。 目盛りラベルの桁数だけを
   * 見る計算なので、 domain と違って全系列で採っても表示内容には影響しない。
   */
  const axisWidthConfig = computeChartAxisConfig({
    demandValues,
    temperatureValues,
  })

  // 左 YAxis 幅を最大桁数の目盛りラベルに応じて動的に広げ、WeatherBar とも共有する
  // (fix #5432)。
  const leftAxisWidth = useMemo(
    () =>
      computeDemandAxisWidth(
        axisWidthConfig.demandTicks,
        CHART_LEFT_AXIS_WIDTH,
      ),
    [axisWidthConfig.demandTicks],
  )
  /**
   * X 軸ラベルの刻み。 プロット幅から 3h / 6h / 12h を選ぶ
   * (design `forecast-today.html:5521-5526` の移植)。
   * 幅を測れない間は `resolveXAxisLabelStepHours` が 3h に倒す。
   *
   * **spec 差異ではない (2026-09-02 に解消)。** 以前は AC-DFD-006a-4 が
   * 「対象日 0:00 起点の 3 時間毎」を**幅の条件なしに**求めていたため間引きは
   * AC 違反で、`[spec 差異 — 未確定]` として記録していた。PdM 判断 (PR #7035) で
   * AC 本文が「**ただし表示幅が狭くラベル同士が重なる場合は、0:00 起点を保ったまま
   * 6 時間毎・12 時間毎の順に間引いてよい**」に改訂され、本実装が正になった
   * (issue #6879)。3 の倍数刻みなので、どの段階でも 0:00 起点は保たれる。
   *
   * プロット幅の差引は左右の margin も含める: 左は `CHART_MARGIN.left` が
   * 負値 (チャート全体を左へ寄せる) なので加算すると実効の予約幅が減り、
   * 右は気温軸が出る時に軸幅と右 margin の両方が塞ぐ。
   */
  const plotWidth = useMemo(
    () =>
      computePlotWidth(
        containerWidth,
        leftAxisWidth + CHART_MARGIN.left,
        CHART_MARGIN.right + (showTemperature ? CHART_RIGHT_AXIS_WIDTH : 0),
      ),
    [containerWidth, leftAxisWidth, showTemperature],
  )
  const xAxisLabelStepHours = useMemo(
    () => resolveXAxisLabelStepHours(plotWidth),
    [plotWidth],
  )
  /**
   * 前端ラベルは**間引きの対象外**なので別軸で判定する。前端と 0:00 は 1 時間
   * (通常ラベルの 1/3) しか離れておらず、刻みを粗くしても距離が変わらない。
   * 詳細は `canShowFrontEdgeLabel` の JSDoc。
   */
  const showFrontEdgeLabel = useMemo(
    () => canShowFrontEdgeLabel(plotWidth),
    [plotWidth],
  )

  const prevLeftAxisWidthRef = useRef<number | undefined>(undefined)
  useLayoutEffect(() => {
    if (prevLeftAxisWidthRef.current !== leftAxisWidth) {
      prevLeftAxisWidthRef.current = leftAxisWidth
      onLeftAxisWidthChange?.(leftAxisWidth)
    }
  }, [leftAxisWidth, onLeftAxisWidthChange])

  // spec AC-005c (demand-forecast-spec-compliance AC-03/04): 需要想定値の
  // 最大電力アノテーション。ドラッグ中プレビュー (effectiveForecastSlots) に
  // 追従させ、kWh 換算 (30分値×1000、千区切り) 済みラベルを算出する
  // (demand-fix-48-koma で瞬時電力換算 ×2 を廃止し kWh 換算に統一)。
  const peakLabel = useMemo(
    () =>
      computePeakLabel(effectiveForecastSlots, isReferenceView ? 0 : gcKoma),
    [effectiveForecastSlots, gcKoma, isReferenceView],
  )

  /**
   * ComposedChart onMouseMove。 ホバー中のコマから気象帯の強調対象の「時」 を解決して
   * 親へ返す。 強調そのものは気象帯 (WeatherBar) が描くので、 ここは通知だけに留める。
   */
  const handleChartMouseMove = (state: MouseHandlerDataParam): void => {
    onHoverWeatherHourChange?.(
      resolveHoverWeatherHour(state.activeTooltipIndex),
    )
  }

  /** ComposedChart onMouseLeave。 チャート外へ出たら強調を解除する。 */
  const handleChartMouseLeave = (): void => {
    onHoverWeatherHourChange?.(undefined)
  }

  /**
   * ComposedChart onMouseDown (design L2151-2177 `_dftOnChartMouseDown` 準拠)。
   */
  const handleChartMouseDown = (
    state: MouseHandlerDataParam,
    event: React.MouseEvent<SVGGraphicsElement>,
  ): void => {
    if (!isEditable || !hasEditRow) return
    // GC 済みコマ・範囲外は resolveDragKoma が undefined を返すためドラッグを
    // 開始しない。前日 2 コマ延長は編集対象外。
    const koma = resolveDragKoma(state.activeTooltipIndex, gcKoma)
    if (koma === undefined) return

    const scale = readYAxisScale(containerRef.current)
    if (scale === undefined) return

    const clickedValue = scale(event.clientY)
    const point = data[komaToChartIndex(koma)]
    const forecastValue =
      typeof point?.forecast === 'number' ? point.forecast : undefined
    const otherValues = otherSeriesKeys
      .filter((key) => !hiddenKeys.has(key))
      .map((key) => {
        const v = point?.[key]
        return typeof v === 'number' ? v : undefined
      })
    // 需要想定値が空のコマ (点なし) は既存の誤掴み防止により選択・掴み不可
    if (!isForecastGrabTarget(clickedValue, forecastValue, otherValues)) return

    event.preventDefault()

    dragKomaRef.current = { koma, scale }
    lastTickRef.current = undefined
    pendingClientYRef.current = undefined
    shiftPressedRef.current = event.shiftKey
    setDragPreview({ koma, value: undefined, slots: forecastSlots })
  }

  const dragLabel =
    dragPreview !== undefined
      ? dragPreview.value !== undefined
        ? t('page:demandForecastDaily.chart.dragEditLabelWithValue', {
            koma: dragPreview.koma,
            // 保存値がそのまま表示値 (送電端 kWh/30min、AC-DFC-011) なので、
            // 他のチャート表示と同じく整形のみを行う。
            value: formatSlotValue(dragPreview.value),
          })
        : t('page:demandForecastDaily.chart.dragEditLabel', {
            koma: dragPreview.koma,
          })
      : undefined

  /* 軸の目盛りは 12px (2026-09-18 デザイナー指摘「グラフの X と Y 軸のフォントは
     12px」)。 [design 差異] 8080 (`js/charts.js`) は左軸 11 / 右軸 10 なので、
     本指摘は 8080 より大きい新しい値。 8080 側も追随させる。 */
  const axisTick = { fontSize: 12, fill: AXIS_COLOR }

  /**
   * X 軸の目盛りラベル。 向き (-45° の斜め表記) の理由は `renderAxisTick` 内のコメント。
   *
   * **縦位置 (`dy`)**: recharts が tick 関数に渡す `y` は
   * 「軸線 + tickSize 6 + tickMargin 2」 なので、 `dy` はそこからのベースライン
   * オフセットになる。 16 は左 Y 軸の最下ラベル 「0」 との間隔を 12.3px 空ける
   * ための値 (デザイナー指摘 2026-08-28「0 と X 軸の時間表記との間隔を少し
   * 開けたい。 くっついて見える」)。 間隔の式は `gap = dy - 3.71` で、 旧値 12 では
   * 8.3px しかなかった。
   *
   * design (`demand/forecast-today.html`) は `dy` 未指定 = recharts 既定
   * (0.71em ≒ 7.81px) で同じ間隔は **4.1px** しかないが、 これは**採らない**。
   * プロトで成立していたのは 00:00 が `textAnchor="start"` で右へ逃げており
   * 「0」 と横方向に 14.9px 離れていたためで、 実装は全ラベルを目盛りに揃えるので
   * その前提が無い (「0」 と横で約 6px 重なる)。
   * よってここは design 準拠ではなく可読性から決める [design 差異]。
   *
   * 上限: XAxis は height 未指定 = recharts 既定 30px なので、 帯に収めるなら
   * `dy <= 22`、 SVG のクリップ境界までなら `dy <= 24` (`dy >= 27` で実際に
   * 見切れる)。 16 は最も内側の上限に対して 6px 手前で、 ラベル下の余白 10px と
   * 上の 12.3px がほぼ均等になる。
   */
  type AxisTickProps = {
    x?: number | string
    y?: number | string
    index?: number
    payload?: { value?: unknown }
  }

  // recharts の `tick` に渡す render 関数。 props を分割代入せず `tickProps.x` の
  // 形で読むのは、 分割代入すると eslint が React component と誤認して
  // `react/prop-types` を要求するため (props は recharts が内部で組み立てるもので
  // 呼び出し側が渡す public API ではない)。
  /**
   * 需要想定線の点。 **掴めるコマにだけ描く** (design `_dftForecastDot`,
   * forecast-today.html:5926-5934)。
   *
   * 掴めるかどうかの判定は、 ドラッグ本体が使うのと**同じ** `resolveDragKoma`
   * (延長部の除外と GC ロックの除外を持つ) に委ねる。 別式で書くと
   * 「点はあるのに掴めない / 掴めるのに点が無い」 がいずれ生じる。
   *
   * 掴めない状態で点を描き続けるのは `conventions.md:266`「表示と実行の乖離」
   * にあたる (BG 集計・確定済み・過去日・GC 済みコマは掴めないのに掴めそうに見える。
   * 2026-08-31 デザイナー指摘)。
   *
   * `pointerEvents: 'none'` は必須。 付けないと点が chart の mousedown を食って
   * ドラッグ (AC-DFD-006) が壊れる (design も同じ理由で付けている)。
   *
   * Recharts は happy-dom で実描画されないため (本ファイル冒頭のコメント参照)
   * 点の有無を描画結果として観測する手段が無い。 代わりに `<Area>` へ渡す
   * renderer 自体を捕まえて代表条件を固定する (`ForecastChart.component.test.tsx`)。
   * 見た目は preview の目視で確認する。
   */
  const renderForecastDot = (dotProps: {
    cx?: number
    cy?: number
    index?: number
    value?: number | null
  }): React.ReactElement | null => {
    const { cx, cy, index, value } = dotProps
    if (cx === undefined || cy === undefined) return null
    if (value === undefined || value === null) return null
    if (!isEditable) return null
    const koma = resolveDragKoma(index, gcKoma)
    if (koma === undefined) return null

    // ドラッグ中は掴んでいる点を大きく塗り、 他はやや大きくして狙いやすくする。
    const isDragging = dragPreview !== undefined
    const isCurrent = dragPreview?.koma === koma
    const r = isDragging ? (isCurrent ? 5 : 3.5) : 2.75
    return (
      <circle
        key={`forecast-dot-${String(koma)}`}
        fill={isCurrent ? FORECAST_COLOR : 'white'}
        stroke={FORECAST_COLOR}
        cx={cx}
        cy={cy}
        r={r}
        strokeWidth={1.5}
        style={{ cursor: 'ns-resize', pointerEvents: 'none' }}
      />
    )
  }

  /**
   * ホバー中のコマに出る強調点。 **掴める点と同じ条件でしか描かない。**
   *
   * 塗り潰し + 大きめ (r=5) はドラッグ中に掴んでいる点と同じ見た目なので、
   * 読み取り専用・BG 集計・GC 済みコマでこれを出すと「掴めないのに掴めそうに
   * 見える」 (= `renderForecastDot` を掴める点だけに絞ったのと同じ理由で
   * `conventions.md:266`「表示と実行の乖離」にあたる。 2026-09-01 レビュー指摘)。
   *
   * 掴めないコマでもホバー位置は `<Tooltip cursor>` の縦線が示すため、 強調点を
   * 出さなくてもどのコマを見ているかは分かる。
   */
  const renderForecastActiveDot = (dotProps: {
    cx?: number
    cy?: number
    index?: number
    value?: number | null
  }): React.ReactElement | null => {
    const { cx, cy, index, value } = dotProps
    if (cx === undefined || cy === undefined) return null
    if (value === undefined || value === null) return null
    if (!isEditable) return null
    if (resolveDragKoma(index, gcKoma) === undefined) return null
    return (
      <circle
        fill={FORECAST_COLOR}
        stroke="white"
        cx={cx}
        cy={cy}
        r={5}
        strokeWidth={2}
        style={{ pointerEvents: 'none' }}
      />
    )
  }

  const renderAxisTick = (tickProps: AxisTickProps): React.ReactElement => {
    // recharts は x / y を number で渡すが型は `number | string`。回転の
    // translate に入れるので数値へ寄せる (座標として使えない値は 0 に倒す)。
    const x = Number(tickProps.x ?? 0)
    const y = Number(tickProps.y ?? 0)
    const index = tickProps.index ?? 0
    const rawValue = tickProps.payload?.value
    const raw = typeof rawValue === 'string' ? rawValue : ''
    const label = isXAxisLabelTick(
      raw,
      index,
      xAxisLabelStepHours,
      showFrontEdgeLabel,
    )
      ? raw
      : ''
    /**
     * ラベルは **-45° の斜め表記** (2026-09-02 デザイナー判断 / issue #6879)。
     *
     * 水平のままだと**前端ラベルが原理的に置けない**。前端「前日23:00」(実測 54px) と
     * 「00:00」(32px) は目盛り中心間が 26px しかなく (範囲拡張 AC-DFD-006a が本体の
     * 手前に前日 23:00〜24:00 を含めるため、この 2 本だけ 1 時間 = 2 コマ間隔)、
     * 中央揃えで 43px 要るところに 26px しか無い。**間引きは「何本目を出すか」を
     * 変えるだけなのでこの距離は変わらず、解決しない。**
     *
     * 倒すと衝突条件が「幅」ではなく「行高」になり、必要な水平送りが
     * 行高 13px ÷ sin45 ≒ 18.4px まで下がって 26px に収まる。これで AC-DFD-006a-4 が
     * 求める前端の軸ラベルを出せる。
     *
     * 回転は `transform` で行い、原点を目盛り位置に合わせる (`x`/`y` は 0 にして
     * translate 側へ寄せる)。`textAnchor="end"` で文字の終端が目盛りに揃い、
     * ラベルは目盛りの左上へ伸びる。
     *
     * `xAxisTickAnchor` の前端特例 (index 0 を `start` にする) は**使わない**。
     * あれは水平ラベルが Y 軸側へはみ出すのを防ぐためのもので、斜めにすると
     * はみ出さないうえ、`end` から外すと回転の基準がずれる。
     */
    return (
      <text
        fill={AXIS_COLOR}
        fontSize={12}
        transform={`translate(${x},${y + 12}) rotate(-45)`}
        textAnchor="end"
      >
        {label}
      </text>
    )
  }

  return (
    <Box
      ref={containerRef}
      // recharts の <svg> は既定 `overflow: hidden` で、-45° の前端ラベル
      // 「前日23:00」は目盛りから左上へ伸びて SVG の左端を割る (実測 6.7px、
      // 2026-09-03 デザイナー指摘「前日23:00 が見切れる」)。SVG の外側にある
      // カードの padding (16px) には収まっている (実測 9.3px の余裕) ので、
      // margin や軸幅を動かさず SVG の overflow だけ visible にして padding に
      // 描かせる。右端の最終ラベルも同じくカード padding に吸わせている
      // (`chartLayout.ts` の #6666 コメント) ため作法は揃う。WeatherBar との
      // 整列 (CHART_MARGIN 共有) には影響しない。
      css={{ '& svg.recharts-surface': { overflow: 'visible' } }}
      style={{
        width: '100%',
        height: chartHeight,
        minHeight: 380,
        position: 'relative',
        cursor: dragPreview !== undefined ? 'ns-resize' : undefined,
      }}
    >
      {dragLabel !== undefined && (
        <Box
          position="absolute"
          zIndex={3}
          top={3}
          left={3}
          background="Surface.accentPrimary"
          borderRadius="full"
          pointerEvents="none"
          paddingX={3}
          paddingY="3px"
        >
          <Text
            textStyle="captionStrong"
            color="Text.highEmphasisInverse"
            whiteSpace="nowrap"
            aria-live="polite"
          >
            {dragLabel}
          </Text>
        </Box>
      )}
      {isTargetDayWeatherMissing && (
        // AC-DFC-013 の案内。 配置はデザイナー判断で design に該当画面が無いため、
        // 気温の右軸がある側 (プロット領域の右上) に置く。 右軸は類似日の気温
        // 等があれば出るので、 その幅を除いたプロット領域の右端に合わせる。
        // 上端には GC 縦線のラベル (`insideTopRight`、 12px) が出るので、 その
        // 1 行下に置き、 縦線・グリッドの上でも読めるよう背景を敷く (実走の
        // 画面で GC ラベルと文字が重なった)。
        <Box
          position="absolute"
          zIndex={2}
          background="Surface.primary"
          pointerEvents="none"
          paddingX={1}
          style={{
            top: CHART_MARGIN.top + NO_WEATHER_DATA_NOTICE_OFFSET_TOP,
            right:
              CHART_MARGIN.right +
              (showTemperature ? CHART_RIGHT_AXIS_WIDTH : 0),
          }}
        >
          <Text textStyle="caption" color="Text.lowEmphasis">
            {t('page:demandForecastDaily.weather.noWeatherData')}
          </Text>
        </Box>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          margin={CHART_MARGIN}
          data={data}
          onMouseDown={handleChartMouseDown}
          onMouseLeave={handleChartMouseLeave}
          onMouseMove={handleChartMouseMove}
        >
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" />
          {/* AC-DFD-006a-4: ラベルは対象日 0:00 起点の 3 時間毎 + 表示範囲の前端。
              `interval={1}` は縦グリッドを 1 時間おき (= 2 コマごと) に保つための
              もので、 ラベルの間引きは tickFormatter が担う。 前端は値ではなく
              位置 (index 0) で判定する (`isXAxisLabelTick` の JSDoc 参照)。 */}
          {/* `height` は**倒したラベルが入る高さ**。-45° の文字は目盛りから左下へ
              伸び、最長「前日23:00」(実測 54px) なら縦に 54 x sin45 ≒ 38px 使う。
              これに目盛り線 6px + tickMargin 2px と、`renderAxisTick` が回転原点を
              目盛りから下げている 12px が乗る。旧値 50 はこの 12px を数えておらず、
              前端ラベルが出る幅で SVG の外へ 15.6px はみ出し、カード下端まで
              1.4px しか残っていなかった (#9238)。50 + 15.6 に余白 4px で 70。
              recharts 既定の 30px のままだと見切れる (review 指摘 #7040)。
              [design 差異] `forecast-today.html` は 50 だが、あちらは 11px の文字を
              recharts 標準の回転で描き 12px 下げていないため、同じ 50 で収まる。 */}
          <XAxis
            height={70}
            dataKey="koma"
            interval={1}
            tick={renderAxisTick}
          />

          {/* item F: 単位サフィックス (旧 `unit={ ${unit}}`) を削除。目盛は数値のみ
              (カンマ区切り)、単位はカード見出し「単位：kWh」が既に担う
              (design forecast-today.html L3170-3172 準拠)。ラベル整形は
              `formatDemandTickLabel` (目盛りの実値をそのまま整形する = 丸めない) に
              委譲する。

              AC-DFD-006a-3: 需要系列が非表示で左軸スケールが未確定の場合でも、
              日境界 ReferenceLine が描画されるよう固定 domain でスケールを確保する。
              需要データが無い時は軸線・目盛りを非表示にする。 */}
          <YAxis
            width={leftAxisWidth}
            allowDataOverflow={!hasVisibleDemandValues}
            axisLine={hasVisibleDemandValues}
            domain={hasVisibleDemandValues ? axisConfig.demandDomain : [0, 1]}
            tick={hasVisibleDemandValues ? axisTick : false}
            tickFormatter={formatDemandTickLabel}
            tickLine={hasVisibleDemandValues}
            ticks={axisConfig.demandTicks}
          />
          {showTemperature && (
            <YAxis
              width={CHART_RIGHT_AXIS_WIDTH}
              domain={axisConfig.temperatureDomain}
              orientation="right"
              tick={{ fontSize: 12, fill: TEMP_FORECAST_COLOR }}
              tickFormatter={(v: number) => `${v}℃`}
              ticks={axisConfig.temperatureTicks}
              yAxisId="temp"
            />
          )}
          {/* AC-DFD-006c: ドラッグ補正中は各系列値ツールチップ (+ cursor 破線縦線) を
              一括で非表示にする (`<Tooltip>` 自体を非描画。design 判断 #2/#3 =
              ポップアップと cursor をまとめて抑止するのが最小差分)。 */}
          {!isChartTooltipSuppressed(dragPreview) && (
            <Tooltip
              // 共通 tooltip (`~/components/common/ChartTooltip` = design
              // `js/charts.js` の `darkTooltipContent` の移植) に寄せる
              // (2026-08-31 デザイナー指摘「グラフの tooltip の UI は 8080 準拠」)。
              // 8080 では 20 画面が同じ helper を共有しており、実装側もスポット価格・
              // 48点データが既にこれを使っている。需要想定だけが Recharts 既定の
              // content のまま取り残されていた。
              //
              // **`formatter` ではなく `formatValue` に書式を載せる。** recharts は
              // `formatter` を `DefaultTooltipContent` の中で適用するので、`content`
              // を差し替えると `formatter` は無視され、kWh が生の小数で出る
              // (= AC-DFD-006d 違反)。ここは差し替え時の唯一の壊しどころ。
              //
              // 表示値の丸め (docs/specs/README.md §数値表示共通ルール):
              // kWh 系列は四捨五入した整数、℃ 系列は入力精度のまま
              // (formatChartTooltipValue の godoc 参照)。描画値 (プロット位置)
              // は小数を保持し、丸めるのはツールチップの表示テキストのみ。
              //
              // 8080 の MWh/kWh 単位トグルは写さない。commit 611905b197 が
              // 「過剰実装の削除」で削った機能で、単位は kWh 統一 (AC-DFC-011)、
              // 表記はカード見出し「単位：kWh」が担う。
              cursor={{
                stroke: AXIS_COLOR,
                strokeWidth: 1,
                strokeDasharray: '3 3',
              }}
              content={<ForecastChartTooltipContent />}
              isAnimationActive={false}
              offset={16}
            />
          )}

          {/* AC-15: 48コマテーブルの列 (時間帯) ホバー時にチャート上の対応ポイントを
              強調する (story demand-003: 気象確認と需要カーブの対応関係を素早く
              把握する)。 */}
          {highlightKoma !== undefined && (
            <ReferenceLine
              stroke={HIGHLIGHT_COLOR}
              strokeDasharray="2 2"
              strokeWidth={2}
              x={komaNumberToTime(highlightKoma)}
            />
          )}

          {/* 需要想定値 (Area + stepAfter)。design 凡例順の先頭 (L2952) */}
          <Area
            fill={FORECAST_COLOR}
            stroke={FORECAST_COLOR}
            activeDot={renderForecastActiveDot}
            connectNulls={false}
            dataKey="forecast"
            dot={renderForecastDot}
            fillOpacity={0.15}
            hide={hiddenKeys.has('forecast')}
            isAnimationActive={false}
            name={label ?? t('page:demandForecastDaily.chart.forecast')}
            strokeWidth={2.5}
            type="stepAfter"
          />

          {/* AC-DFD-006a-1: 延長部の需要想定 (前日・翌日の業務確定) の参考線。編集できない
              参考値なので需要想定値と別の薄い破線で描き、凡例には載せない (design 準拠)。 */}
          {preForecastSlots !== undefined && (
            <Line
              stroke={EXTENSION_FORECAST_COLOR}
              connectNulls={false}
              dataKey={EXTENSION_FORECAST_PREV_KEY}
              dot={false}
              isAnimationActive={false}
              name={t('page:demandForecastDaily.chart.previousDayReference')}
              strokeDasharray="2 3"
              strokeWidth={1.5}
              type="stepAfter"
            />
          )}
          {postForecastSlots !== undefined && (
            <Line
              stroke={EXTENSION_FORECAST_COLOR}
              connectNulls={false}
              dataKey={EXTENSION_FORECAST_NEXT_KEY}
              dot={false}
              isAnimationActive={false}
              name={t('page:demandForecastDaily.chart.nextDayReference')}
              strokeDasharray="2 3"
              strokeWidth={1.5}
              type="stepAfter"
            />
          )}

          {/* spec AC-005c (demand-forecast-spec-compliance AC-03/04): 需要想定値の
              最大電力アノテーション。ピークが無い (全コマ空、翌々日初期状態等) 場合は
              非表示。ラベルは kWh 換算 (30分値×1000、千区切り) 済みの「最大 X,XXX kWh」
              形式 (demand-fix-48-koma で瞬時電力換算 ×2 を廃止)、ライン位置は
              チャートの表示スケール (kWh) 上のピークコマ値。
              AC-03 (demand-forecast-daily-v4): 最小電力ラインは削除済み。 */}
          {!hiddenKeys.has('forecast') && peakLabel !== undefined && (
            <ReferenceLine
              stroke={FORECAST_COLOR}
              ifOverflow="extendDomain"
              label={{
                value: t('page:demandForecastDaily.chart.maxLabel', {
                  value: peakLabel.kwhLabel,
                }),
                position: 'insideTopLeft',
                fill: FORECAST_COLOR,
                fontSize: 10,
                fontWeight: 600,
              }}
              strokeDasharray="2 4"
              strokeOpacity={0.55}
              y={effectiveForecastSlots[peakLabel.koma - 1]}
            />
          )}

          {/* 当日実績エリア (design 凡例順 2番目、当日断面のみ) */}
          {hasTodayActual && (
            <Area
              fill={TODAY_ACTUAL_AREA_COLOR}
              stroke={TODAY_ACTUAL_AREA_COLOR}
              connectNulls={false}
              dataKey="todayActual"
              dot={
                emphasisOf('todayActual').isEmphasized
                  ? emphasizedDot(TODAY_ACTUAL_AREA_COLOR)
                  : false
              }
              // proto demand/forecast-today.html の実績面と同じ (減光時は面も同じ割合で薄める)
              fillOpacity={0.28 * emphasisOf('todayActual').opacity}
              hide={hiddenKeys.has('todayActual')}
              isAnimationActive={false}
              name={t('page:demandForecastDaily.chart.todayActual')}
              strokeOpacity={emphasisOf('todayActual').opacity}
              strokeWidth={
                emphasisOf('todayActual').isEmphasized
                  ? emphasizedStrokeWidth(1.5)
                  : 1.5
              }
              type="stepAfter"
            />
          )}

          {/* AC-03 (demand-forecast-daily-v4): 前日実績系列 (design のビルトイン
              `prevDay` 系列) は過剰実装のため削除済み。 */}

          {/* 過去実績スロット (design 凡例順 4番目、「実績 MM/DD」)。
              需要想定 過去実績「採用」の小売別取込 Design Note Decision 3:
              小売別解決後 (selectPastActualColumn) に値が 1 点も無い日
              (分解不能 / 当該小売の帰属行なし) は Line も凡例
              (chartLegend.ts) も生成しない — 「持たない」の判定は配列長でなく
              定義済みスロットの有無で行う (下の過去実績気温/露点と同じ理由・
              同じ述語 hasDefinedProtoSlotValue、pastDewSeriesDescriptors の
              JSDoc 参照)。idx は元の pastActuals 配列 index を維持したまま
              スキップする (気温/露点系列との位置対応を崩さない)。 */}
          {pastActuals.map((pa, idx) => {
            if (!hasDefinedProtoSlotValue(pa.slots)) return null
            const dateStr =
              pa.date !== undefined
                ? `${String(pa.date.month)}/${String(pa.date.day)}`
                : t('page:demandForecastDaily.chart.pastActual')
            const emphasis = emphasisOf(`past_${String(idx)}`)
            const color = PAST_COLORS[idx % PAST_COLORS.length]
            return (
              <Line
                key={`past_${String(idx)}`}
                stroke={color}
                connectNulls={false}
                dataKey={`past_${String(idx)}`}
                dot={emphasis.isEmphasized ? emphasizedDot(color) : false}
                hide={hiddenKeys.has(`past_${String(idx)}`)}
                isAnimationActive={false}
                name={`${t('page:demandForecastDaily.chart.pastActualPrefix')} ${dateStr}`}
                strokeDasharray={
                  emphasis.isEmphasized
                    ? undefined
                    : PAST_DASHES[idx % PAST_DASHES.length]
                }
                strokeOpacity={emphasis.opacity}
                strokeWidth={
                  emphasis.isEmphasized ? emphasizedStrokeWidth(1.5) : 1.5
                }
                type="stepAfter"
              />
            )
          })}

          {/* 確定履歴参照列 (AC-DFD-031, s07-confirm-history-import-reference-
              view.md §UI「チャート系列」)。label は
              formatConfirmRecordPreviewLabel が生成した「確定 MM/DD」形式を
              そのまま使う (過去実績と異なり日付から都度組み立てない)。
              temperature / dewpoint は確定履歴に存在しないため描画しない。 */}
          {confirmRecordRefs.map((ref, idx) => {
            const emphasis = emphasisOf(`confirmRef_${ref.id}`)
            const color = CONFIRM_REF_COLORS[idx % CONFIRM_REF_COLORS.length]
            return (
              <Line
                key={`confirmRef_${ref.id}`}
                stroke={color}
                connectNulls={false}
                dataKey={`confirmRef_${ref.id}`}
                dot={emphasis.isEmphasized ? emphasizedDot(color) : false}
                hide={hiddenKeys.has(`confirmRef_${ref.id}`)}
                isAnimationActive={false}
                name={ref.label}
                strokeDasharray={
                  emphasis.isEmphasized
                    ? undefined
                    : CONFIRM_REF_DASHES[idx % CONFIRM_REF_DASHES.length]
                }
                strokeOpacity={emphasis.opacity}
                strokeWidth={
                  emphasis.isEmphasized ? emphasizedStrokeWidth(1.5) : 1.5
                }
                type="stepAfter"
              />
            )
          })}

          {/* 過去実績気温 (AC-23, spec AC-024): 選択過去日の実績気温を気温軸
              (yAxisId="temp") に反映する。temperatureSlots が空 (backend
              graceful degrade / 未取得) の日はスキップする。 */}
          {showTemperature &&
            pastActuals.map((pa, idx) => {
              if (pa.temperatureSlots.length === 0) return null
              const dateStr =
                pa.date !== undefined
                  ? `${String(pa.date.month)}/${String(pa.date.day)}`
                  : t('page:demandForecastDaily.chart.pastActual')
              // 同じ日の需要と同じ key で強調・減光する (AC-DFD-006a-6「過去日の気温・露点も強調の対象」)
              const emphasis = emphasisOf(`past_${String(idx)}`)
              const color = PAST_COLORS[idx % PAST_COLORS.length]
              return (
                <Line
                  key={`pastTemp_${String(idx)}`}
                  stroke={color}
                  connectNulls={false}
                  dataKey={`pastTemp_${String(idx)}`}
                  dot={emphasis.isEmphasized ? emphasizedDot(color) : false}
                  hide={hiddenKeys.has(`pastTemp_${String(idx)}`)}
                  isAnimationActive={false}
                  name={`${t('page:demandForecastDaily.chart.temperature')} ${t('page:demandForecastDaily.chart.pastActualPrefix')} ${dateStr}`}
                  strokeDasharray={
                    emphasis.isEmphasized
                      ? undefined
                      : PAST_DASHES[idx % PAST_DASHES.length]
                  }
                  strokeOpacity={emphasis.opacity}
                  strokeWidth={
                    emphasis.isEmphasized ? emphasizedStrokeWidth(1) : 1
                  }
                  type="linear"
                  yAxisId="temp"
                />
              )
            })}

          {/* 過去実績露点 (AC-05, spec AC-024): pastDewSeriesDescriptors が
              期待値 (id・軸・トグル key) を固定した過去日露点系列を描画する。
              descriptor を経由しない配線ミスは純関数テストでは検知できず、
              JSX 配線自体の実行検証は F6 fixme 解除まで残らない (§9 残論点、
              happy-dom で Recharts 系列が DOM 描画されないため)。 */}
          {showTemperature &&
            pastDewDescriptors.map((descriptor) => {
              // descriptor.id = `pastDew_{idx}` — idx は元の pastActuals 配列
              // index (pastDewSeriesDescriptors が露点なし日をスキップするため、
              // descriptor 配列内の position とは一致しない)。id から復元する。
              const idx = Number(descriptor.id.slice('pastDew_'.length))
              const pa = pastActuals[idx]
              const dateStr =
                pa?.date !== undefined
                  ? `${String(pa.date.month)}/${String(pa.date.day)}`
                  : t('page:demandForecastDaily.chart.pastActual')
              // 同じ日の需要と同じ key で強調・減光する (過去実績気温と同じ)
              const emphasis = emphasisOf(`past_${String(idx)}`)
              const color = PAST_COLORS[idx % PAST_COLORS.length]
              return (
                <Line
                  key={descriptor.id}
                  stroke={color}
                  connectNulls={false}
                  dataKey={descriptor.id}
                  dot={emphasis.isEmphasized ? emphasizedDot(color) : false}
                  hide={hiddenKeys.has(descriptor.toggleKey)}
                  isAnimationActive={false}
                  name={`${t('page:demandForecastDaily.chart.dewpoint')} ${t('page:demandForecastDaily.chart.pastActualPrefix')} ${dateStr}`}
                  strokeDasharray={
                    emphasis.isEmphasized
                      ? undefined
                      : PAST_DASHES[idx % PAST_DASHES.length]
                  }
                  strokeOpacity={emphasis.opacity}
                  strokeWidth={
                    emphasis.isEmphasized ? emphasizedStrokeWidth(1) : 1
                  }
                  type="linear"
                  yAxisId={descriptor.yAxisId}
                />
              )
            })}

          {/* 気温予測 (右軸 ℃、design 凡例順 6番目) */}
          {showTemperature && hasTempForecast && (
            <Line
              stroke={TEMP_FORECAST_COLOR}
              connectNulls
              dataKey="tempForecast"
              dot={false}
              hide={hiddenKeys.has('tempForecast')}
              isAnimationActive={false}
              name={t('page:demandForecastDaily.chart.temperature')}
              strokeDasharray="4 3"
              strokeOpacity={emphasisOf('tempForecast').opacity}
              strokeWidth={2}
              type="linear"
              yAxisId="temp"
            />
          )}

          {/* --- design に無いが実装済みの系列 (末尾に維持) --- */}

          {/* 実績トレンド予測破線 */}
          {hasTrendForecast && (
            <Line
              stroke={TREND_FORECAST_COLOR}
              connectNulls={false}
              dataKey="trendForecast"
              dot={false}
              hide={hiddenKeys.has('trendForecast')}
              isAnimationActive={false}
              name={t('page:demandForecastDaily.chart.trendForecast')}
              strokeDasharray="5 3"
              strokeOpacity={emphasisOf('trendForecast').opacity}
              strokeWidth={2}
              type="linear"
            />
          )}

          {/* 気温実績 (右軸 ℃) */}
          {showTemperature && hasTempActual && (
            <Line
              stroke={TEMP_ACTUAL_COLOR}
              connectNulls={false}
              dataKey="tempActual"
              dot={false}
              hide={hiddenKeys.has('tempActual')}
              isAnimationActive={false}
              name={t('page:demandForecastDaily.chart.temperatureActual')}
              strokeOpacity={emphasisOf('tempActual').opacity}
              strokeWidth={2}
              type="linear"
              yAxisId="temp"
            />
          )}

          {/* 露点 (実績のみ。予測露点は spec #4290 改訂 2026-08-14 で撤去済み
              — JMA 公開予報が湿度を配信しないため常に空スロットだった) */}
          {showTemperature &&
            hasDewpointData &&
            (dewpointActualSlots !== undefined ||
              preDewpointActualSlots !== undefined) && (
              <Line
                stroke={DEW_ACTUAL_COLOR}
                connectNulls={false}
                dataKey="dewActual"
                dot={false}
                hide={hiddenKeys.has('dewpoint')}
                isAnimationActive={false}
                name={t('page:demandForecastDaily.chart.dewpointActual')}
                strokeOpacity={emphasisOf('dewActual').opacity}
                strokeWidth={2}
                type="linear"
                yAxisId="temp"
              />
            )}

          {/* AC-DFD-006a-3: 範囲内の 2 つの日境界 (前日→対象日 / 対象日→翌日) を太い縦線で強調。 */}
          <ReferenceLine
            zIndex={1000}
            stroke={AXIS_COLOR}
            strokeDasharray="4 4"
            strokeWidth={2}
            x={komaNumberToTime(1)}
          />
          {/* 後端が参考領域であることを示す注記 (design `forecast-today.html` の
              nextday-boundary)。 GC ラベル (insideTopRight) と重ならないよう下に置く。 */}
          <ReferenceLine
            zIndex={1000}
            stroke={AXIS_COLOR}
            label={{
              value: t('page:demandForecastDaily.chart.nextDayBoundaryLabel'),
              position: 'insideBottomRight',
              fill: LightModeSemanticColors.Text.lowEmphasis.value,
              fontSize: 10,
              fontWeight: 600,
            }}
            strokeDasharray="4 4"
            strokeWidth={2}
            x={komaNumberToTime(49)}
          />

          {/* AC-DFD-006a-5: 当日断面だけ、次に GC でロックされるコマ (画面上部の GC
              カウントダウンと同じ) の位置に GC 縦線を出す。線より左がロック済み。
              日境界 (灰の 2px 破線) ・ピーク (細い点線) と色・太さで区別し、系列より前に描く
              (design `forecast-today.html` の GC 縦線: --surface-negative-high・3px・6 3)。 */}
          {gcLineKoma !== undefined && (
            <ReferenceLine
              zIndex={1001}
              stroke={GC_LINE_COLOR}
              label={{
                value: t('page:demandForecastDaily.chart.gcKomaLabel', {
                  koma: gcLineKoma,
                }),
                position: 'insideTopRight',
                fill: GC_LINE_COLOR,
                fontSize: 12,
                fontWeight: 700,
              }}
              strokeDasharray="6 3"
              strokeWidth={3}
              x={komaNumberToTime(gcLineKoma)}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
